import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Clock, XClose } from "@untitledui/icons";
import { AIChatWindow } from "@/components/application/ai-chat/ai-chat-window";
import { ChatTabs } from "@/components/application/ai-chat/chat-tabs";
import { ChatHistorySidebar } from "@/components/application/ai-chat/chat-history-sidebar";
import { useChatAI } from "@/components/application/ai-chat/chat-ai-context";
import { Button } from "@/components/base/buttons/button";
import { Tooltip } from "@/components/base/tooltip/tooltip";

export type AskEvePanelVariant = "shipper" | "receiver" | "freight_forwarder";

type AskEvePanelContentProps = {
    variant: AskEvePanelVariant;
    /** Mostra il bottone chiudi nell'header (pannello laterale) */
    showClose?: boolean;
    onClose?: () => void;
};

export function AskEvePanelContent({ variant, showClose, onClose }: AskEvePanelContentProps) {
    const { t } = useTranslation(["common", "pages:dashboard", "pages:quotes"]);
    const { createNewChat } = useChatAI();
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    const subtitle =
        variant === "receiver" ? (
            <p className="text-tertiary mt-1">{t("receiver.subtitle", { ns: "pages:dashboard" })}</p>
        ) : variant === "freight_forwarder" ? (
            <p className="text-tertiary mt-1">
                {t("dashboard.subtitle", { ns: "pages:quotes" }) || "Overview of your quotes and statistics"}
            </p>
        ) : null;

    return (
        <div className="flex h-full min-h-0 flex-col bg-primary">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-secondary px-4 py-3">
                <div className="min-w-0">
                    <h1 className="text-lg font-semibold text-primary">{t("askEve.title", { ns: "common" })}</h1>
                    {subtitle}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <Tooltip title={t("aiChat.newChat")} placement="bottom">
                        <Button onClick={createNewChat} color="secondary" size="sm" iconLeading={Plus} />
                    </Tooltip>
                    <Tooltip title={t("aiChat.chatHistory")} placement="bottom">
                        <Button
                            onClick={() => setIsHistoryOpen(true)}
                            color="secondary"
                            size="sm"
                            iconLeading={Clock}
                        />
                    </Tooltip>
                    {showClose && onClose ? (
                        <Tooltip title={t("actions.close", { ns: "common" })} placement="bottom">
                            <Button
                                onClick={onClose}
                                color="secondary"
                                size="sm"
                                iconLeading={XClose}
                                aria-label={t("actions.close", { ns: "common" })}
                            />
                        </Tooltip>
                    ) : null}
                </div>
            </div>
            <ChatTabs />
            <div className="min-h-0 flex-1">
                <AIChatWindow />
            </div>
            <ChatHistorySidebar isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} />
        </div>
    );
}
